/**
 * Tour interactivo del Dashboard de Recursos Humanos
 *
 * Ruta activa: /alerts-persons  →  DashboardHumanResource
 * Prefijo CSS: DHR__ (DashboardHumanResource)
 *
 * Flujo automático:
 *  1. Contenedor principal — visión general
 *  2. Barra de controles: búsqueda + toggle de filtros
 *  3. Campo de búsqueda
 *  4. Botón ☰ → ABRE el panel de filtros (onHighlightStarted)
 *  5. Panel de filtros — cuatro selectores (rol, estado-doc, tipo-doc, activo)
 *  6. Toggle de tipos de documento → ABRE el subpanel colapsable (onHighlightStarted)
 *  7. Panel de checkboxes de tipos
 *  8. Checkbox "sin documentos seleccionados" (filtro de faltantes)
 *  9. Layout principal → CIERRA ambos paneles (onHighlightStarted)
 * 10. Panel lateral de usuarios
 * 11. Lista de usuarios (tarjetas con indicadores)
 * 12. Paginación
 * 13. Panel de documentos — estado vacío (sin usuario elegido)
 *
 * @param {string} theme     — tema activo ('light' | 'dark')
 * @param {object} driverRef — ref con la instancia de driver.js
 */
export const createHumanResourceTour = (theme, driverRef) => {
  const t = theme;

  // ── Helpers DOM ────────────────────────────────────────────────────────

  const openMainFilters = () => {
    const isOpen = !!document.querySelector(`.DHR__${t}-dashboard-hr-filters-panel`);
    if (!isOpen) document.querySelector(`.DHR__${t}-dashboard-hr-filter-toggle`)?.click();
  };

  const closeMainFilters = () => {
    const isOpen = !!document.querySelector(`.DHR__${t}-dashboard-hr-filters-panel`);
    if (isOpen) document.querySelector(`.DHR__${t}-dashboard-hr-filter-toggle`)?.click();
  };

  const openDocTypesPanel = () => {
    const isOpen = !!document.querySelector(`.DHR__${t}-dashboard-hr-doc-types-panel`);
    if (!isOpen) document.querySelector(`.DHR__${t}-dashboard-hr-doc-types-toggle`)?.click();
  };

  const closeDocTypesPanel = () => {
    const isOpen = !!document.querySelector(`.DHR__${t}-dashboard-hr-doc-types-panel`);
    if (isOpen) document.querySelector(`.DHR__${t}-dashboard-hr-doc-types-toggle`)?.click();
  };

  return [
    // ── 1. Contenedor principal ──────────────────────────────────────────
    {
      element: `.DHR__${t}-dashboard-hr`,
      popover: {
        title: 'Dashboard de Documentos — RR. HH.',
        description:
          'Vista centralizada del estado documental de todo el personal. ' +
          'A la izquierda se lista el personal con indicadores visuales de alerta; ' +
          'a la derecha se muestran los documentos del empleado seleccionado. ' +
          'Todos los filtros actúan en tiempo real sobre la lista.',
        side: 'top',
        align: 'start',
      },
    },

    // ── 2. Área de controles ─────────────────────────────────────────────
    {
      element: `.DHR__${t}-dashboard-hr-controls`,
      popover: {
        title: 'Controles de búsqueda y filtrado',
        description:
          'Barra principal que concentra la búsqueda de texto libre y el acceso ' +
          'al panel de filtros avanzados. Los cambios se aplican automáticamente ' +
          'con un breve retardo para no saturar el servidor.',
        side: 'bottom',
        align: 'start',
      },
    },

    // ── 3. Campo de búsqueda ─────────────────────────────────────────────
    {
      element: `.DHR__${t}-dashboard-hr-search-wrap`,
      popover: {
        title: 'Buscar personal',
        description:
          'Escribe el nombre, correo electrónico o número de documento del empleado. ' +
          'La búsqueda se lanza automáticamente tras 1 segundo sin escribir. ' +
          'El botón <strong>×</strong> limpia el campo y resetea los resultados al instante.',
        side: 'bottom',
        align: 'start',
      },
    },

    // ── 4. Toggle de filtros → ABRE panel (onHighlightStarted) ──────────
    {
      element: `.DHR__${t}-dashboard-hr-filter-toggle`,
      onHighlightStarted: openMainFilters,
      popover: {
        title: 'Filtros avanzados — abriendo',
        description:
          'El panel de filtros se ha abierto automáticamente. ' +
          'El botón <strong>☰</strong> actúa como interruptor: vuelve a pulsarlo ' +
          'para contraer el panel cuando ya no necesites los filtros.',
        side: 'bottom',
        align: 'end',
      },
    },

    // ── 5. Panel de filtros ──────────────────────────────────────────────
    {
      element: `.DHR__${t}-dashboard-hr-filters-panel`,
      popover: {
        title: 'Filtros del panel',
        description:
          'Cuatro selectores combinables entre sí:<br><br>' +
          '• <strong>Rol</strong>: limita la lista a un rol concreto (Operador, RRHH, etc.).<br>' +
          '• <strong>Estado documental</strong>: muestra solo empleados con documentos ' +
          'vencidos, próximos a vencer, vacíos, obligatorios o adicionales.<br>' +
          '• <strong>Tipo de documento</strong>: cambia qué categoría se consulta ' +
          '(<em>Obligatorios</em> o <em>Adicionales</em>).<br>' +
          '• <strong>Estado del usuario</strong>: filtra entre activos, inactivos o todos.',
        side: 'bottom',
        align: 'start',
      },
    },

    // ── 6. Toggle de tipos de documento → ABRE subpanel ─────────────────
    {
      element: `.DHR__${t}-dashboard-hr-doc-types-toggle`,
      onHighlightStarted: openDocTypesPanel,
      popover: {
        title: 'Tipos de documento — abriendo',
        description:
          'Subpanel colapsable que permite seleccionar uno o más tipos de documento ' +
          'específicos. El badge numérico indica cuántos tipos están activos. ' +
          'El botón <strong>Limpiar</strong> elimina toda la selección con un clic.',
        side: 'bottom',
        align: 'start',
      },
    },

    // ── 7. Panel de checkboxes de tipos ─────────────────────────────────
    {
      element: `.DHR__${t}-dashboard-hr-doc-types-panel`,
      popover: {
        title: 'Selección múltiple de tipos',
        description:
          'Lista de los tipos de documento disponibles según el rol seleccionado. ' +
          'Puedes marcar varios simultáneamente para obtener empleados que tengan ' +
          '<em>cualquiera</em> de los tipos elegidos (o ninguno, si activas el filtro ' +
          'de documentos faltantes).<br><br>' +
          '<strong>Nota:</strong> si no has seleccionado un rol, aparecen todos los tipos.',
        side: 'bottom',
        align: 'start',
      },
    },

    // ── 8. Checkbox de documentos faltantes ─────────────────────────────
    {
      element: `.DHR__${t}-dashboard-hr-checkbox-label--prominent`,
      popover: {
        title: 'Mostrar usuarios sin los documentos seleccionados',
        description:
          'Cuando está activo, el sistema <strong>invierte</strong> la búsqueda: ' +
          'en lugar de mostrar quiénes <em>tienen</em> los tipos marcados, muestra ' +
          'quiénes <em>no los tienen</em>.<br><br>' +
          'Este filtro solo se habilita cuando hay al menos un tipo de documento ' +
          'seleccionado en la lista superior.',
        side: 'top',
        align: 'start',
      },
    },

    // ── 9. Layout principal → CIERRA ambos paneles (onHighlightStarted) ──
    {
      element: `.DHR__${t}-dashboard-hr-layout`,
      onHighlightStarted: () => {
        closeDocTypesPanel();
        setTimeout(() => closeMainFilters(), 150);
      },
      popover: {
        title: 'Diseño principal',
        description:
          'El dashboard se divide en dos paneles lado a lado: ' +
          'la <strong>lista de usuarios</strong> a la izquierda y los ' +
          '<strong>documentos del usuario seleccionado</strong> a la derecha. ' +
          'En pantallas pequeñas, ambos paneles se muestran de forma apilada.',
        side: 'top',
        align: 'start',
      },
    },

    // ── 10. Panel lateral de usuarios ────────────────────────────────────
    {
      element: `.DHR__${t}-dashboard-hr-users-panel`,
      popover: {
        title: 'Panel de usuarios',
        description:
          'Lista paginada de empleados que coinciden con los filtros activos. ' +
          'El encabezado indica el total de resultados y, si hay una búsqueda activa, ' +
          'la muestra entre comillas.',
        side: 'right',
        align: 'start',
      },
    },

    // ── 11. Lista de usuarios ────────────────────────────────────────────
    {
      element: `.DHR__${t}-dashboard-hr-users-list`,
      popover: {
        title: 'Tarjetas de usuario',
        description:
          'Cada tarjeta muestra el nombre, correo, rol y un resumen documental:<br>' +
          '• <strong>Docs cargados / total</strong>: proporción de documentos cubiertos.<br>' +
          '• <strong>Oblig / Adic</strong>: desglose por categoría.<br>' +
          '• <strong>Vencidos</strong> (rojo): documentos con fecha expirada.<br>' +
          '• <strong>Por vencer</strong> (ámbar): documentos con menos de 30 días.<br><br>' +
          'Haz clic en una tarjeta para ver todos sus documentos en el panel derecho.',
        side: 'right',
        align: 'start',
      },
    },

    // ── 12. Paginación ───────────────────────────────────────────────────
    {
      element: `.DHR__${t}-dashboard-hr-pagination`,
      popover: {
        title: 'Paginación',
        description:
          'Navega entre páginas de resultados con los botones <strong>←</strong> y ' +
          '<strong>→</strong>. El indicador central muestra la página actual y el ' +
          'total de páginas disponibles. El número de filas por página se ajusta ' +
          'automáticamente según la altura del contenedor.',
        side: 'top',
        align: 'center',
      },
    },

    // ── 13. Panel de documentos (estado vacío) ───────────────────────────
    {
      element: `.DHR__${t}-dashboard-hr-docs-panel`,
      popover: {
        title: 'Panel de documentos',
        description:
          'Al seleccionar un empleado, este panel muestra todos sus documentos ' +
          'organizados en dos categorías: <strong>Obligatorios</strong> y ' +
          '<strong>Adicionales</strong>.<br><br>' +
          'Cada documento incluye su estado (Vigente, Por vencer, Vencido, Vacío), ' +
          'el archivo adjunto con previsualización y el botón de edición para ' +
          'actualizar o agregar el archivo directamente desde el perfil.',
        side: 'left',
        align: 'start',
      },
    },
  ];
};
